import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { LogOut } from 'lucide-react'
import { getV1MeRetrieveQueryOptions, useV1MeRetrieve } from '@/api/client'
import { AuthShell } from '@/components/auth-shell'
import { AvatarPortrait } from '@/components/profile/dicebear-avatar'
import { SessionTimeoutWidget } from '@/components/session-timeout-widget'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { authApi } from '@/lib/auth-api'
import { sessionQuery } from '@/lib/auth'
import { avatarSeed } from '@/lib/avatar-catalog'
import { resetRejectedMeSession, studentAccessRedirect } from '@/lib/me-route-access'
import { useIdleLogout } from '@/lib/use-idle-logout'

export const Route = createFileRoute('/uczen/')({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    if (!session.isAuthenticated) {
      throw redirect({ to: '/uczen/login' })
    }
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    const accessRedirect = studentAccessRedirect(me)
    if (accessRedirect === '/uczen/login') {
      resetRejectedMeSession(context.queryClient)
      throw redirect({ to: '/uczen/login' })
    }
    if (accessRedirect === '/') {
      throw redirect({ to: '/' })
    }
  },
  component: StudentHome,
})

function StudentHome() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { data: me } = useV1MeRetrieve()
  const user = me?.status === 200 ? me.data : undefined
  const nickname = user?.nickname || 'uczniu'
  // `avatar` niesie wyłącznie StudentMe — `beforeLoad` odsyła stąd konta
  // pracownicze, ale unia `/me/` i tak wymaga zawężenia.
  const avatar = user && 'avatar' in user ? user.avatar : undefined
  // TR-200 R7: sesja webowa ucznia z krótkim oknem nieaktywności i widgetem.
  const idle = useIdleLogout(true, '/uczen/login')

  const handleLogout = async () => {
    await authApi.logout()
    queryClient.clear()
    router.history.push('/uczen/login')
  }

  return (
    <AuthShell>
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center text-sm">
          {/* alt pusty: powitanie tuż niżej niesie tę samą tożsamość, więc obraz
              jest tu dekoracyjny. */}
          <AvatarPortrait
            options={avatar}
            seed={avatarSeed(user && 'login' in user ? user.login : 'loading')}
            alt=""
            size="lg"
          />
          <p className="font-medium">Cześć, {nickname}!</p>
          <p className="text-muted-foreground">
            Strefa ucznia w przygotowaniu — korzystaj na razie z aplikacji mobilnej.
          </p>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="size-4" /> Wyloguj
          </Button>
        </CardContent>
      </Card>
      <SessionTimeoutWidget secondsLeft={idle.secondsLeft} onExtend={idle.extend} />
    </AuthShell>
  )
}
